import React, { useState, useRef, useEffect, useCallback } from 'react'

const SeekBar = ({
  handleSeek,
  handleSeekOver,
  convertVideoSecondstoMintues,
  seekBarHoverPercent,
  duration,
  currentTime,
  bufferPercent,
  playerData,
  showControlBar,
}) => {
  let data = playerData.control_bar.data
  const [isDragging, setIsDragging] = useState(false)
  const [isHovering, setIsHovering] = useState(false)
  const [dragPosition, setDragPosition] = useState(null)
  const seekBarRef = useRef(null)
  const animationFrameRef = useRef(null)

  const handleMouseDown = useCallback(
    (e) => {
      setIsDragging(true)
      handleSeek(e)
    },
    [handleSeek],
  )

  useEffect(() => {
    if (!isDragging) {
      setDragPosition(null)
      return
    }

    const handleMouseMoveGlobal = (e) => {
      if (seekBarRef.current) {
        const rect = seekBarRef.current.getBoundingClientRect()
        const offsetX = Math.max(0, Math.min(e.clientX - rect.left, rect.width))

        // Update drag position for smooth visual feedback
        setDragPosition((offsetX / rect.width) * 100)

        // Cancel previous animation frame
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current)
        }

        // Use requestAnimationFrame for smooth updates
        animationFrameRef.current = requestAnimationFrame(() => {
          const syntheticEvent = {
            nativeEvent: {
              offsetX: offsetX,
            },
            target: {
              parentNode: seekBarRef.current,
            },
          }

          handleSeek(syntheticEvent)
        })
      }
    }

    const handleMouseUpGlobal = () => {
      setIsDragging(false)
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }

    document.addEventListener('mousemove', handleMouseMoveGlobal)
    document.addEventListener('mouseup', handleMouseUpGlobal)

    return () => {
      document.removeEventListener('mousemove', handleMouseMoveGlobal)
      document.removeEventListener('mouseup', handleMouseUpGlobal)
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [isDragging, handleSeek])

  // Get handle size and position based on hover/drag state
  const getHandleSize = () => {
    const isActive = isHovering || isDragging

    switch (data.seek_bar_type) {
      case 'ELLIPSE':
        return {
          height: showControlBar ? (isActive ? '18px' : '14px') : '0px',
          width: isActive ? '8px' : '6.5px',
          top: isActive ? '-7px' : '-5px',
        }
      case 'RECTANGLE':
        return {
          height: showControlBar ? (isActive ? '14px' : '10px') : '0px',
          width: isActive ? '7px' : '5px',
          top: isActive ? '-5px' : '-3px',
        }
      case 'ROUND':
        return {
          height: showControlBar ? (isActive ? '16px' : '10px') : '0px',
          width: isActive ? '16px' : '10px',
          top: isActive ? '-6px' : '-3px',
        }
      default:
        return null
    }
  }

  const handleSize = getHandleSize()

  // Use drag position when dragging, otherwise use actual current time
  const displayWidth =
    isDragging && dragPosition !== null ? dragPosition : (currentTime / duration) * 100

  return (
    <div
      ref={seekBarRef}
      className={`w-full relative`}
      style={{
        background: `${data.seek_bar_color}60`,
        height: '4px',
        cursor: isDragging ? 'grabbing' : 'pointer',
      }}
      onMouseDown={handleMouseDown}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onMouseOver={(e) => handleSeekOver(e)}
      title={`${convertVideoSecondstoMintues(Math.floor(seekBarHoverPercent * duration))}`}
    >
      <div
        className="absolute h-full left-0 top-0"
        style={{
          width: `${displayWidth}%`,
          background: data.play_progress_bar_color,
          zIndex: 20,
          transition: isDragging ? 'none' : 'width 0.1s linear',
          willChange: isDragging ? 'width' : 'auto',
        }}
      >
        {data.seek_bar_type === 'SIMPLE' && <></>}
        {data.seek_bar_type === 'ELLIPSE' && handleSize && (
          <div
            style={{
              height: handleSize.height,
              width: handleSize.width,
              right: 0,
              top: handleSize.top,
              background: data.seek_bar_handle_color,
              borderRadius: '10px',
              transition: 'all 0.15s cubic-bezier(0.4, 0.0, 0.2, 1)',
              willChange: isHovering || isDragging ? 'height, width, top' : 'auto',
            }}
            className="absolute"
          />
        )}
        {data.seek_bar_type === 'RECTANGLE' && handleSize && (
          <div
            style={{
              height: handleSize.height,
              width: handleSize.width,
              right: 0,
              top: handleSize.top,
              background: data.seek_bar_handle_color,
              transition: 'all 0.15s cubic-bezier(0.4, 0.0, 0.2, 1)',
              willChange: isHovering || isDragging ? 'height, width, top' : 'auto',
            }}
            className="absolute"
          />
        )}
        {data.seek_bar_type === 'ROUND' && handleSize && (
          <div
            style={{
              height: handleSize.height,
              width: handleSize.width,
              right: 0,
              top: handleSize.top,
              background: data.seek_bar_handle_color,
              transition: 'all 0.15s cubic-bezier(0.4, 0.0, 0.2, 1)',
              boxShadow: isHovering || isDragging ? '0 0 8px rgba(0,0,0,0.3)' : 'none',
              willChange: isHovering || isDragging ? 'height, width, top' : 'auto',
            }}
            className="absolute rounded-full"
          />
        )}
      </div>
      <div
        className="absolute h-full left-0 top-0"
        style={{
          width: `${bufferPercent}%`,
          background: '#ffffff30',
          zIndex: 5,
        }}
      />
    </div>
  )
}

export default SeekBar
